import React, { createContext, useContext } from 'react';

/**
 * The authenticated agent's Teller ID, sourced from their user record (the BFF
 * resolves it from the IdP claim / interim sandbox directory and returns it on
 * `/api/session`). Provided once at the app root and consumed by the screens
 * that previously prompted the agent to type a Teller ID (ID&V, Customer
 * Search, Call History).
 *
 * `undefined` ⇒ the user has no assigned Teller ID (e.g. Admin, or an explicit
 * Admin-Console removal), in which
 * case those screens fall back to a manual entry field. This keeps the existing
 * behaviour for users without a Teller ID and for any screen rendered outside
 * the provider (e.g. unit tests), where the default value is `undefined`.
 *
 * See issue #4: "Teller ID should not be an entry field — it should be stored
 * in the user database."
 */
const AgentTellerContext = createContext<string | undefined>(undefined);

export function AgentTellerProvider({
  tellerId,
  children,
}: {
  tellerId?: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <AgentTellerContext.Provider value={tellerId}>
      {children}
    </AgentTellerContext.Provider>
  );
}

/** The agent's session Teller ID, or undefined when none is assigned. */
export function useAgentTeller(): string | undefined {
  return useContext(AgentTellerContext);
}
